// Opening a patient or a staff record from the clinician dashboard tables.
//
// These are UI entry-point events, not state changes. Product needs them so
// that after release we can compare the new name-click against the existing
// View Patient button / pencil icon — both now fire the same event, split by
// the reserved `trigger` field. They are not a substitute for
// assessment_overview_viewed (that still fires on overview mount) or for
// staff_account_event (that still records the edit that landed).
//
// Emitted from the browser because the click never reaches a callable. A
// failure here must never block navigation or opening the modal: the clinician
// still gets to the record, we just lose the comparison row.

import { emitAnalyticsEvent } from './emitEvent';

/** Never let an analytics failure surface in a dashboard action. */
function safeEmit(fn) {
  try {
    return fn();
  } catch (error) {
    return false;
  }
}

const PATIENT_TRIGGERS = new Set(['name', 'action_button']);
const PATIENT_CASELOADS = new Set(['current', 'archived']);
const STAFF_TRIGGERS = new Set(['name', 'action_icon']);

/**
 * A clinician opened a patient from the Current or Archived caseload table.
 *
 * @param {object} opts
 * @param {string} opts.patientId
 * @param {'name'|'action_button'} opts.trigger Which control they used.
 * @param {'current'|'archived'} opts.caseload Which list they were on.
 */
export function emitCaseloadPatientOpened({ patientId, trigger, caseload } = {}) {
  return safeEmit(() => {
    if (!patientId) return false;
    if (!PATIENT_TRIGGERS.has(trigger)) return false;
    if (!PATIENT_CASELOADS.has(caseload)) return false;
    return emitAnalyticsEvent('assessment_caseload_patient_opened', {
      patient_id: String(patientId),
      trigger,
      caseload,
    });
  });
}

/**
 * An administrator opened Edit Provider Details from the staff table.
 *
 * @param {object} opts
 * @param {string} opts.staffId The clinicianId of the row they opened.
 * @param {'name'|'action_icon'} opts.trigger Which control they used.
 */
export function emitStaffEditOpened({ staffId, trigger } = {}) {
  return safeEmit(() => {
    if (!staffId) return false;
    if (!STAFF_TRIGGERS.has(trigger)) return false;
    return emitAnalyticsEvent('staff_edit_opened', {
      staff_id: String(staffId),
      trigger,
    });
  });
}
